// app/components/ResumeSavePanel.jsx
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

function cn(...cls) {
  return cls.filter(Boolean).join(" ");
}

function safeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function sanitizeFilename(s = "") {
  return s.trim().replace(/[\s]+/g, "_").replace(/[^a-zA-Z0-9_\-\.]+/g, "");
}

export default function ResumeSavePanel({
  resumeText = "",                    // extracted/edited resume text
  suggestions = [],                   // AI suggestions array
  auditResults = null,               // audit results object
  originalFileName = "",             // original uploaded file name
  defaultName = "Resume_Analysis",   // prefill for save name
  storageKey = "resume-analysis",    // localStorage namespace
  onLoad,                           // optional: (savedData) => void
  className = "",                   // optional wrapper class
}) {
  const [name, setName] = useState(defaultName);
  const [saved, setSaved] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");
  const importRef = useRef(null);

  const LS_KEY = `resume_saver_${storageKey}`;

  // Load saved analyses
  useEffect(() => {
    setIsLoading(true);
    try {
      const raw = localStorage.getItem(LS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      setSaved(Array.isArray(arr) ? arr : []);
      setErr("");
    } catch (e) {
      setErr("Failed to read saved items.");
    } finally {
      setIsLoading(false);
    }
  }, [LS_KEY]);

  // Persist helper
  const persist = (next) => {
    setSaved(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch (e) {
      setErr("Could not persist to localStorage.");
    }
  };

  const sorted = useMemo(
    () => [...saved].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [saved]
  );

  const canSave = resumeText && resumeText.trim().length > 0;
  const fileName = `${sanitizeFilename(name || defaultName)}.txt`;

  // Create the data object to save
  const createSaveData = () => ({
    id: safeId(),
    name: name?.trim() || `${defaultName} ${new Date().toLocaleString()}`,
    originalFileName,
    resumeText,
    suggestions: suggestions || [],
    auditResults: auditResults || null,
    wordCount: resumeText ? resumeText.trim().split(/\s+/).length : 0,
    charCount: resumeText ? resumeText.length : 0,
    createdAt: new Date().toISOString(),
  });

  const handleSave = () => {
    if (!canSave) return;
    const item = createSaveData();
    persist([...saved, item]);
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(resumeText || "");
    } catch {
      setErr("Clipboard copy failed.");
    }
  };

  const handleCopyAnalysis = async () => {
    try {
      const analysisText = formatAnalysisForExport(createSaveData());
      await navigator.clipboard.writeText(analysisText);
    } catch {
      setErr("Clipboard copy failed.");
    }
  };

  const formatAnalysisForExport = (data) => {
    let output = [];
    
    output.push("=".repeat(60));
    output.push(`RESUME ANALYSIS: ${data.name}`);
    output.push(`Created: ${new Date(data.createdAt).toLocaleString()}`);
    if (data.originalFileName) {
      output.push(`Original File: ${data.originalFileName}`);
    }
    output.push(`Word Count: ${data.wordCount}`);
    output.push(`Character Count: ${data.charCount}`);
    output.push("=".repeat(60));
    output.push("");

    // Resume text
    output.push("RESUME TEXT:");
    output.push("-".repeat(40));
    output.push(data.resumeText || "");
    output.push("");

    // AI Suggestions
    if (data.suggestions && data.suggestions.length > 0) {
      output.push("AI SUGGESTIONS:");
      output.push("-".repeat(40));
      data.suggestions.forEach((sug, i) => {
        output.push(`${i + 1}. ${sug.title || "Suggestion"} [${sug.severity || "tip"}]`);
        if (sug.summary) output.push(`   Summary: ${sug.summary}`);
        if (sug.rationale) output.push(`   Rationale: ${sug.rationale}`);
        if (sug.example_before) output.push(`   Before: ${sug.example_before}`);
        if (sug.example_after) output.push(`   After: ${sug.example_after}`);
        output.push("");
      });
    }

    // Audit Results
    if (data.auditResults) {
      output.push("AUDIT RESULTS:");
      output.push("-".repeat(40));
      output.push(JSON.stringify(data.auditResults, null, 2));
      output.push("");
    }

    return output.join("\n");
  };

  const handleDownload = () => {
    if (!canSave) return;
    const data = createSaveData();
    const content = formatAnalysisForExport(data);
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: fileName });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadItem = (item) => {
    const content = formatAnalysisForExport(item);
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `${sanitizeFilename(item.name || defaultName)}.txt`,
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const loadItem = (item) => {
    if (typeof onLoad === "function") {
      onLoad(item);
    }
    setName(item.name || defaultName);
  };

  const deleteItem = (id) => {
    persist(saved.filter((s) => s.id !== id));
  };

  const exportAll = () => {
    const blob = new Blob([JSON.stringify(saved, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `${sanitizeFilename(storageKey)}_analyses.json`,
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImportClick = () => importRef.current?.click();

  const handleImport = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || "[]"));
        const arr = Array.isArray(parsed) ? parsed : [];
        // Merge by id; keep existing if conflict
        const byId = new Map(saved.map((s) => [s.id, s]));
        for (const item of arr) {
          if (!item || typeof item !== "object") continue;
          if (!item.id) item.id = safeId();
          if (!byId.has(item.id)) byId.set(item.id, item);
        }
        persist([...byId.values()]);
        setErr("");
      } catch {
        setErr("Import failed: invalid JSON.");
      } finally {
        if (importRef.current) importRef.current.value = "";
      }
    };
    reader.onerror = () => {
      setErr("Import failed: could not read file.");
      if (importRef.current) importRef.current.value = "";
    };
    reader.readAsText(f);
  };

  return (
    <section
      className={cn(
        "rounded-2xl border border-gray-200 bg-white shadow-sm",
        "p-4 sm:p-5",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            Save Resume Analysis
          </h3>
          <p className="text-sm text-gray-700">
            Save your resume text, AI suggestions, and audit results locally or download as a file.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Analysis name"
            className="w-48 sm:w-64 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-600"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className={cn(
              "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition active:scale-[.98]",
              canSave
                ? "bg-gradient-to-r from-teal-600 to-orange-600 text-white hover:from-teal-700 hover:to-orange-700"
                : "bg-white text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
            )}
            title="Save to browser"
          >
            Save
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleDownload}
          disabled={!canSave}
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition active:scale-[.98]",
            canSave
              ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
              : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
          )}
          title={`Download analysis as ${fileName}`}
        >
          Download Analysis
        </button>
        
        <button
          type="button"
          onClick={handleCopyText}
          disabled={!canSave}
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition active:scale-[.98]",
            canSave
              ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
              : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
          )}
          title="Copy resume text to clipboard"
        >
          Copy Text
        </button>

        <button
          type="button"
          onClick={handleCopyAnalysis}
          disabled={!canSave}
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition active:scale-[.98]",
            canSave
              ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
              : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
          )}
          title="Copy full analysis to clipboard"
        >
          Copy Analysis
        </button>

        <button
          type="button"
          onClick={exportAll}
          disabled={saved.length === 0}
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-semibold shadow-sm transition active:scale-[.98]",
            saved.length > 0
              ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
              : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
          )}
        >
          Export All (.json)
        </button>

        <input
          ref={importRef}
          type="file"
          accept="application/json"
          onChange={handleImport}
          className="hidden"
        />
        <button
          type="button"
          onClick={onImportClick}
          className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-semibold shadow-sm transition active:scale-[.98] bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
        >
          Import (.json)
        </button>

        {err && (
          <span className="ml-auto text-xs text-orange-700">{err}</span>
        )}
      </div>

      {/* Current analysis info */}
      {canSave && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="text-sm font-semibold text-slate-900 mb-2">Current Analysis</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-gray-500">Words:</span>
              <span className="ml-1 font-medium text-slate-900">
                {resumeText.trim().split(/\s+/).length}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Characters:</span>
              <span className="ml-1 font-medium text-slate-900">
                {resumeText.length}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Suggestions:</span>
              <span className="ml-1 font-medium text-slate-900">
                {suggestions.length}
              </span>
            </div>
            {originalFileName && (
              <div>
                <span className="text-gray-500">File:</span>
                <span className="ml-1 font-medium text-slate-900 truncate">
                  {originalFileName}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Saved list */}
      <div className="mt-5">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">
            Saved Analyses <span className="text-gray-500 font-normal">({saved.length})</span>
          </h4>
          {isLoading && (
            <span className="text-xs text-gray-500">Loading…</span>
          )}
        </div>

        {saved.length === 0 ? (
          <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700">
            No analyses saved yet. Click <span className="font-semibold">Save</span> to store this analysis in your browser.
          </div>
        ) : (
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {sorted.map((item) => (
              <li
                key={item.id}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {item.name}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      Saved {new Date(item.createdAt).toLocaleString()}
                    </div>
                    {item.originalFileName && (
                      <div className="mt-0.5 text-xs text-gray-500 truncate">
                        From: {item.originalFileName}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
                    .txt
                  </span>
                </div>

                {/* Stats */}
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Words:</span>
                    <div className="font-medium text-slate-900">{item.wordCount || 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Tips:</span>
                    <div className="font-medium text-slate-900">{(item.suggestions || []).length}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Audit:</span>
                    <div className="font-medium text-slate-900">{item.auditResults ? "Yes" : "No"}</div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-3 max-h-20 overflow-hidden rounded-md bg-gray-100 p-2 text-[11px] leading-tight text-gray-700 ring-1 ring-inset ring-gray-200">
                  {String(item.resumeText || "").slice(0, 200)}
                  {String(item.resumeText || "").length > 200 ? "…": ""}
                </div>

                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => loadItem(item)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                    title="Load this analysis"
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadItem(item)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                    title="Download file"
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteItem(item.id)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                    title="Delete analysis"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}